import type {
  DsdEditorValueMatch,
  DsdEditorValueOccurrence,
} from "@/apis/dsd-editor/route";
import type { DsdEditorSelectedCellSummary } from "@/utils/dsd-editor/document-items";
import {
  getSelectableOccurrenceId,
  getValueMatchCellIds,
  getValueMatchKey,
  occurrenceMatchesId,
  valueMatchIncludesId,
} from "@/utils/dsd-editor/note-reference-review";
import { useCallback, useEffect, useMemo, useState } from "react";

type NoteReferenceSelection = {
  cellId: string | null;
  valueMatchKey: string | null;
  /**
   * 패널 맥락의 기준 셀. 값 후보가 아닌 셀(텍스트 연결 주석 셀 등)을 패널 카드로 골랐을 때
   * 선택은 그 셀로 옮기되 패널·연결 강조는 원래 본문 셀 기준을 유지하기 위한 값.
   * 값 후보는 match 안에 있어 필요 없다(null).
   */
  anchorCellId: string | null;
};

const EMPTY_NOTE_REFERENCE_SELECTION: NoteReferenceSelection = {
  cellId: null,
  valueMatchKey: null,
  anchorCellId: null,
};

export const useNoteReferenceReviewSelection = ({
  valueMatches,
  renderedCellIds,
  selectedCellSummaryById,
  requestCellScroll,
}: {
  valueMatches: DsdEditorValueMatch[];
  renderedCellIds: ReadonlySet<string>;
  selectedCellSummaryById: ReadonlyMap<string, DsdEditorSelectedCellSummary>;
  requestCellScroll: (cellId: string) => void;
}) => {
  const [selection, setSelection] = useState<NoteReferenceSelection>(
    EMPTY_NOTE_REFERENCE_SELECTION,
  );
  const selectedCellId = selection.cellId;
  const activeValueMatchKey = selection.valueMatchKey;
  const anchorCellId = selection.anchorCellId;
  /** 패널이 check를 찾는 기준 셀 — 기준 셀이 있으면 그것, 아니면 선택 셀 */
  const contextCellId = anchorCellId ?? selectedCellId;
  const selectedCellSummary: DsdEditorSelectedCellSummary | null =
    selectedCellId
      ? (selectedCellSummaryById.get(selectedCellId) ?? null)
      : null;
  const activeValueMatch = useMemo(() => {
    if (!selectedCellId) return null;

    if (activeValueMatchKey) {
      const keyedMatch = valueMatches.find(
        (match, matchIndex) =>
          getValueMatchKey(match, matchIndex) === activeValueMatchKey &&
          (valueMatchIncludesId(match, selectedCellId) ||
            (!!anchorCellId &&
              occurrenceMatchesId(match.source, anchorCellId))),
      );
      if (keyedMatch) return keyedMatch;
    }

    return (
      valueMatches.find((match) =>
        occurrenceMatchesId(match.source, selectedCellId),
      ) ??
      valueMatches.find((match) =>
        valueMatchIncludesId(match, selectedCellId),
      ) ??
      null
    );
  }, [activeValueMatchKey, anchorCellId, selectedCellId, valueMatches]);
  const linkedCellIds = useMemo(() => {
    if (!selectedCellId) return new Set<string>();

    // 기준 셀은 match가 없어도 연결로 강조한다
    const ids = activeValueMatch ? getValueMatchCellIds(activeValueMatch) : [];
    if (anchorCellId) ids.push(anchorCellId);

    return new Set(
      ids.filter(
        (cellId) => cellId !== selectedCellId && renderedCellIds.has(cellId),
      ),
    );
  }, [activeValueMatch, anchorCellId, renderedCellIds, selectedCellId]);

  // 선택과 무관하게 "적어도 한 match에서 후보로 잡힌" 모든 렌더된 셀 — 항상 하이라이트
  const candidateCellIds = useMemo(() => {
    const ids = new Set<string>();
    for (const match of valueMatches) {
      for (const cellId of getValueMatchCellIds(match)) {
        if (renderedCellIds.has(cellId)) ids.add(cellId);
      }
    }
    return ids;
  }, [valueMatches, renderedCellIds]);

  const selectCell = useCallback(
    (cellId: string) => {
      const sourceMatchIndex = valueMatches.findIndex((match) =>
        occurrenceMatchesId(match.source, cellId),
      );
      if (sourceMatchIndex >= 0) {
        const sourceMatch = valueMatches[sourceMatchIndex];
        setSelection({
          cellId,
          valueMatchKey: sourceMatch
            ? getValueMatchKey(sourceMatch, sourceMatchIndex)
            : null,
          anchorCellId: null,
        });
        return;
      }

      if (activeValueMatch && valueMatchIncludesId(activeValueMatch, cellId)) {
        setSelection((prev) => ({ ...prev, cellId }));
        return;
      }

      const relatedMatchIndex = valueMatches.findIndex((match) =>
        valueMatchIncludesId(match, cellId),
      );
      const relatedMatch = valueMatches[relatedMatchIndex];
      setSelection({
        cellId,
        valueMatchKey: relatedMatch
          ? getValueMatchKey(relatedMatch, relatedMatchIndex)
          : null,
        anchorCellId: null,
      });
    },
    [activeValueMatch, valueMatches],
  );

  const clearSelection = useCallback(() => {
    setSelection(EMPTY_NOTE_REFERENCE_SELECTION);
  }, []);

  // 매칭 항목 호버 미리보기 — 해당 셀만 남기고 나머지 흐리게 (선택과 별개, ring 없음)
  const [previewCellId, setPreviewCellId] = useState<string | null>(null);
  const previewOccurrence = useCallback(
    (occurrence: DsdEditorValueOccurrence | null) => {
      const cellId = occurrence
        ? getSelectableOccurrenceId(occurrence, renderedCellIds)
        : null;
      setPreviewCellId(cellId);
    },
    [renderedCellIds],
  );
  // 선택이 바뀌면(설정/해제) 남은 호버 미리보기 정리 — 선택이 우선
  useEffect(() => {
    setPreviewCellId(null);
  }, [selectedCellId]);

  const selectOccurrence = useCallback(
    (
      occurrence: DsdEditorValueOccurrence,
      match: DsdEditorValueMatch | null,
    ) => {
      const matchIndex = match ? valueMatches.indexOf(match) : -1;
      const nextValueMatchKey =
        match && matchIndex >= 0 ? getValueMatchKey(match, matchIndex) : null;
      const nextSelectedId = getSelectableOccurrenceId(
        occurrence,
        renderedCellIds,
      );

      if (nextSelectedId) {
        // match 밖의 셀(텍스트 연결 주석 셀 등)이면 지금 보고 있던 본문 셀을 기준으로 남긴다
        const inMatch = !!match && valueMatchIncludesId(match, nextSelectedId);
        setSelection((prev) => ({
          cellId: nextSelectedId,
          valueMatchKey: nextValueMatchKey,
          anchorCellId: inMatch
            ? null
            : (prev.anchorCellId ??
              (match
                ? getSelectableOccurrenceId(match.source, renderedCellIds)
                : prev.cellId)),
        }));
        requestCellScroll(nextSelectedId);
        return;
      }

      setSelection((prev) => ({
        ...prev,
        valueMatchKey: nextValueMatchKey,
      }));
    },
    [renderedCellIds, requestCellScroll, valueMatches],
  );

  // "돌아갈 셀" — 매칭이 있으면 그 본문(source) 셀, 아니면 anchor 나 선택 셀. 스크롤 버튼의 표시 조건과
  // scrollToSelectedCell 이 같은 값을 써야 "보이는데 엉뚱한 데로 감" 이 안 생긴다
  const scrollTargetCellId = useMemo(() => {
    if (!selectedCellId) return null;
    return activeValueMatch
      ? getSelectableOccurrenceId(activeValueMatch.source, renderedCellIds)
      : (anchorCellId ?? selectedCellId);
  }, [activeValueMatch, anchorCellId, renderedCellIds, selectedCellId]);

  const scrollToSelectedCell = useCallback(() => {
    if (!scrollTargetCellId) return;

    setSelection((prev) => ({
      ...prev,
      cellId: scrollTargetCellId,
      anchorCellId: null,
    }));
    requestCellScroll(scrollTargetCellId);
  }, [scrollTargetCellId, requestCellScroll]);

  return {
    selectedCellId,
    contextCellId,
    selectedCellSummary,
    activeValueMatch,
    linkedCellIds,
    candidateCellIds,
    selectCell,
    clearSelection,
    selectOccurrence,
    scrollTargetCellId,
    scrollToSelectedCell,
    previewCellId,
    previewOccurrence,
  };
};
